import { RequireAuthAction } from "@/components/auth/RequireAuthAction";
import { TokenIcon } from "@/components/brand/TokenIcon";
import { AnimatedNumber } from "@/components/defi/AnimatedNumber";
import { ConfirmSheet } from "@/components/defi/ConfirmSheet";
import { TokenSelectSheet } from "@/components/defi/TokenSelectSheet";
import { useMmkSwapParams } from "@/hooks/useGovernance";
import {
	useLiquidityTreasury,
	useTreasuryOverview,
} from "@/hooks/useTreasury";
import { useAuth } from "@/lib/auth";
import { depositableTokenMax } from "@/lib/amount-limits";
import { copyToClipboard } from "@/lib/clipboard";
import { docsArticlePath } from "@/lib/docs/parse";
import { mmkSpotPrice } from "@/lib/mmk-curve";
import { getIcConfig } from "@/lib/network";
import {
	amountToNat,
	getConfig,
	getV2ProtocolActor,
	hasProtocolEnv,
	natToAmount,
} from "@/lib/protocol-actor";
import { ROUTES } from "@/lib/routes";
import { normalizeTokenId, type ExtendedTokenId } from "@/lib/token-assets";
import { formatAmountPlain, formatNum, parseAmount } from "@/lib/utils";
import {
	quoteAssetForUsdmLocal,
	quoteUsdmForAssetLocal,
	swapAssetForUsdm,
	swapUsdmForAsset,
	toCandidCollateral,
	type DeskAsset,
} from "@motokomaker/ic-client";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowDownUp, KeyRound, Zap } from "lucide-react";
import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";

const DESK_ASSETS: DeskAsset[] = ["ICP", "ckBTC", "ckETH"];

function isDeskAsset(t: ExtendedTokenId): t is DeskAsset {
	return t === "ICP" || t === "ckBTC" || t === "ckETH";
}

/**
 * DeFi Liquidity: bilateral oracle desk + treasury inventory.
 * PegSwap (ckUSDC) stays on /pegswap. Integrator API keys in Agent Layer.
 */
export default function LiquidityPage() {
	const { data: sleeve, isLoading } = useLiquidityTreasury();
	const { data: overview } = useTreasuryOverview();
	const { data: mmkParams } = useMmkSwapParams();
	const { balances, refreshBalances, ensureProtocolActor } = useAuth();
	const qc = useQueryClient();
	const protocolId = getIcConfig().protocolCanisterId?.trim() || "";
	const surplus = overview?.surplusUsdm ?? sleeve?.surplusMaster ?? 0;
	const mmkSpot = mmkSpotPrice(mmkParams?.totalSoldMmk ?? 0);

	const [asset, setAsset] = useState<DeskAsset>("ICP");
	const [buyAsset, setBuyAsset] = useState(true); // true = USDM→asset
	const [amount, setAmount] = useState("10");
	const [confirmOpen, setConfirmOpen] = useState(false);
	const [pending, setPending] = useState(false);

	const feeBps = sleeve?.deskFeeBps ?? 30;
	const deskEnabled = sleeve?.deskEnabled ?? true;

	const priceQ = useQuery({
		queryKey: ["v2", "liquidity", "price", asset],
		queryFn: async () => {
			const a = await getV2ProtocolActor();
			if (!a || !hasProtocolEnv()) return null;
			const q = await a.quoteSwapUsdmForAsset(
				toCandidCollateral(asset),
				amountToNat(1),
			);
			return {
				price: natToAmount(q.assetPriceE8),
				inventory: natToAmount(q.inventoryE8),
				enabled: Boolean(q.enabled),
			};
		},
		staleTime: 15_000,
		enabled: hasProtocolEnv(),
	});

	const price = priceQ.data?.price ?? 0;
	const inventory = priceQ.data?.inventory ?? sleeveInventory(sleeve, asset);

	const payToken: ExtendedTokenId = buyAsset ? "USDM" : asset;
	const recvToken: ExtendedTokenId = buyAsset ? asset : "USDM";

	const maxBal =
		payToken === "USDM"
			? balances.USDM
			: payToken === "ICP"
				? balances.ICP
				: payToken === "ckBTC"
					? balances.ckBTC
					: payToken === "ckETH"
						? balances.ckETH
						: 0;
	const maxSpendable = depositableTokenMax(maxBal);

	const quote = useMemo(() => {
		const n = parseAmount(amount);
		if (buyAsset) {
			const q = quoteUsdmForAssetLocal(n, price || 1, feeBps);
			return { out: q.assetOut, fee: q.fee };
		}
		const q = quoteAssetForUsdmLocal(n, price || 1, feeBps);
		return { out: q.usdmOut, fee: q.fee };
	}, [amount, buyAsset, price, feeBps]);

	const flip = () => setBuyAsset((v) => !v);

	async function executeDesk() {
		const n = parseAmount(amount);
		if (!Number.isFinite(n) || n <= 0) {
			toast.error("Enter a valid amount");
			return;
		}
		if (!deskEnabled || priceQ.data?.enabled === false) {
			toast.error("Liquidity desk is paused on-chain");
			return;
		}
		if (buyAsset && n > 0 && inventory <= 0) {
			toast.error(`No ${asset} inventory in the sleeve`);
			return;
		}
		if (n > maxSpendable + 1e-12) {
			toast.error(
				`Amount exceeds spendable ${payToken} (wallet minus ledger fees)`,
			);
			return;
		}
		setPending(true);
		try {
			if (!hasProtocolEnv()) {
				toast.error("Connect Internet Identity with protocol env for live desk");
				return;
			}
			const pair = await ensureProtocolActor();
			if (!pair) {
				toast.error("Protocol actor not ready — Connect and retry");
				return;
			}
			const config = getConfig();
			const ledgers = await pair.actor.getLedgerCanisters();
			if (buyAsset) {
				const paid = await swapUsdmForAsset(
					pair.agent,
					pair.actor,
					config,
					ledgers,
					asset,
					n,
					amountToNat,
				);
				toast.success(
					`Bought ${natToAmount(paid).toFixed(6)} ${asset} with ${amount} USDM`,
				);
			} else {
				const out = await swapAssetForUsdm(
					pair.agent,
					pair.actor,
					config,
					ledgers,
					asset,
					n,
					amountToNat,
				);
				toast.success(
					`Sold ${amount} ${asset} → ${natToAmount(out).toFixed(4)} USDM`,
				);
			}
			setConfirmOpen(false);
			void refreshBalances();
			void qc.invalidateQueries({ queryKey: ["v2", "public", "liquidityTreasury"] });
			void qc.invalidateQueries({ queryKey: ["v2", "liquidity"] });
			void qc.invalidateQueries({ queryKey: ["v2", "public", "treasuryOverview"] });
		} catch (err) {
			toast.error(err instanceof Error ? err.message : "Desk swap failed");
		} finally {
			setPending(false);
		}
	}

	return (
		<div className="container mx-auto space-y-8 px-4 py-6 sm:py-8 pb-16">
			<div id="section-inventory" className="scroll-mt-24">
				<p className="section-eyebrow">Liquidity</p>
				<h1 className="text-xl font-bold tracking-tight sm:text-2xl">
					Liquidity desk & inventory
				</h1>
				<p className="mt-1 max-w-2xl text-sm text-muted-foreground">
					Oracle-priced USDM ↔ ICP / ckBTC / ckETH on-protocol (not an AMM). Fees
					credit surplus; selling coll mints USDM and does not debit surplus.
					ckUSDC stays on PegSwap.
				</p>
				<p className="mt-2 text-xs">
					<Link
						to={docsArticlePath("treasury", "liquidity-sleeve")}
						className="font-semibold text-primary underline"
					>
						Docs · how it works
					</Link>
					{" · "}
					<Link
						to={docsArticlePath("treasury", "surplus-accounting")}
						className="font-semibold text-primary underline"
					>
						Surplus & floors
					</Link>
					{" · "}
					<Link
						to={ROUTES.pegswap}
						className="font-semibold text-primary underline"
					>
						PegSwap
					</Link>
				</p>
			</div>

			<section
				id="section-trade"
				className="action-panel scroll-mt-24 space-y-4"
			>
				<div className="flex flex-wrap items-center justify-between gap-2">
					<div>
						<p className="section-eyebrow">Desk</p>
						<h2 className="text-base font-bold">Trade with sleeves</h2>
					</div>
					<span className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] font-semibold text-primary">
						oracle − {feeBps} bps
						{sleeve
							? deskEnabled
								? sleeve.source === "live"
									? " · live"
									: " · fixture"
								: " · paused"
							: " · …"}
					</span>
				</div>

				<div className="grid grid-cols-2 gap-2 rounded-xl border border-border/60 bg-muted/30 p-3 text-[11px] sm:grid-cols-4">
					<div>
						<p className="uppercase text-muted-foreground">{asset} inventory</p>
						<p className="font-mono font-semibold">
							{inventory.toLocaleString(undefined, { maximumFractionDigits: 6 })}
						</p>
					</div>
					<div>
						<p className="uppercase text-muted-foreground">Oracle</p>
						<p className="font-mono font-semibold">
							{price > 0 ? `$${formatNum(price, 2)}` : "—"}
						</p>
					</div>
					<div>
						<p className="uppercase text-muted-foreground">Surplus floor</p>
						<p className="font-mono font-semibold">
							{formatNum(sleeve?.minSurplusHealth ?? 10_000, 0)} USDM
						</p>
					</div>
					<div>
						<p className="uppercase text-muted-foreground">Ops WC floor</p>
						<p className="font-mono font-semibold">
							{formatNum(sleeve?.minOpsUsdmHealth ?? 5_000, 0)} USDM
						</p>
					</div>
				</div>

				<div className="rounded-2xl border border-border bg-muted/50 p-4">
					<div className="flex items-center justify-between text-xs text-muted-foreground">
						<span>You pay</span>
						<button
							type="button"
							className="min-h-touch font-semibold text-primary"
							onClick={() =>
								setAmount(
									maxSpendable > 0
										? formatAmountPlain(maxSpendable, 6)
										: amount,
								)
							}
						>
							Max {maxSpendable > 0 ? formatAmountPlain(maxSpendable, 4) : "—"}
						</button>
					</div>
					<div className="mt-2 flex min-w-0 items-center gap-2">
						<input
							value={amount}
							onChange={(e) => setAmount(e.target.value)}
							className="min-w-0 flex-1 bg-transparent font-mono-amount text-2xl font-semibold outline-none"
							inputMode="decimal"
							aria-label="Desk trade amount"
						/>
						{buyAsset ? (
							<span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2 py-1.5 text-sm font-semibold">
								<TokenIcon token="USDM" size="sm" plain />
								USDM
							</span>
						) : (
							<TokenSelectSheet
								value={asset}
								options={[...DESK_ASSETS]}
								onChange={(t) => {
									if (isDeskAsset(t)) setAsset(t);
								}}
							/>
						)}
					</div>
				</div>

				<div className="flex justify-center">
					<button
						type="button"
						aria-label="Flip direction"
						className="inline-flex size-11 items-center justify-center rounded-full border border-border bg-card shadow-sm"
						onClick={flip}
					>
						<ArrowDownUp className="size-4" />
					</button>
				</div>

				<div className="rounded-2xl border border-border bg-muted/30 p-4">
					<p className="text-xs text-muted-foreground">You receive (est.)</p>
					<div className="mt-2 flex items-center justify-between gap-2">
						<p className="font-mono-amount text-2xl font-semibold">
							<AnimatedNumber value={quote.out} decimals={buyAsset ? 6 : 4} />
						</p>
						{buyAsset ? (
							<TokenSelectSheet
								value={asset}
								options={[...DESK_ASSETS]}
								onChange={(t) => {
									if (isDeskAsset(t)) setAsset(t);
								}}
							/>
						) : (
							<span className="inline-flex items-center gap-1.5 rounded-lg border border-border bg-card px-2 py-1.5 text-sm font-semibold">
								<TokenIcon token="USDM" size="sm" plain />
								USDM
							</span>
						)}
					</div>
				</div>

				<p className="text-[11px] text-muted-foreground">
					Fee ≈ {formatNum(quote.fee, 4)} USDM → surplus.{" "}
					{buyAsset
						? "Net USDM is burned; payout from sleeve inventory."
						: "Protocol mints USDM; surplus is not spent for the payout."}
				</p>

				<RequireAuthAction
					labelWhenUnauthed="Connect to trade"
					disabled={pending}
					onAuthenticated={() => setConfirmOpen(true)}
				>
					{pending ? "Working…" : `${buyAsset ? "Buy" : "Sell"} ${asset}`}
				</RequireAuthAction>
			</section>

			<section className="card-surface grid gap-3 p-5 sm:grid-cols-2">
				<div>
					<p className="text-[10px] font-semibold uppercase text-muted-foreground">
						Treasury protocol canister
					</p>
					{protocolId ? (
						<button
							type="button"
							className="mt-1 break-all text-left font-mono text-xs font-semibold text-foreground hover:text-primary"
							title="Copy principal"
							onClick={() => {
								void copyToClipboard(protocolId).then((ok) =>
									ok
										? toast.success("Protocol principal copied")
										: toast.error("Copy failed"),
								);
							}}
						>
							{protocolId}
						</button>
					) : (
						<p className="mt-1 font-mono text-xs text-muted-foreground">
							Not configured
						</p>
					)}
					<p className="mt-1 text-[11px] text-muted-foreground">
						Holds sleeve assets (ops ICP, MMK, ckBTC, ckETH).
					</p>
				</div>
				<div>
					<p className="text-[10px] font-semibold uppercase text-muted-foreground">
						Surplus & health floors
					</p>
					<p className="mt-1 font-mono-amount text-lg font-bold">
						{formatNum(surplus, 2)} USDM
					</p>
					<p className="mt-1 text-sm text-muted-foreground">
						Master floor{" "}
						<span className="font-mono font-semibold text-foreground">
							{formatNum(sleeve?.minSurplusHealth ?? 10_000, 0)}
						</span>
						{" · "}
						Ops WC{" "}
						<span className="font-mono font-semibold text-foreground">
							{formatNum(sleeve?.opsUsdm ?? 0, 2)}
						</span>
						{" / floor "}
						<span className="font-mono font-semibold text-foreground">
							{formatNum(sleeve?.minOpsUsdmHealth ?? 5_000, 0)}
						</span>
					</p>
				</div>
			</section>

			<section className="space-y-3">
				<div className="flex flex-wrap items-end justify-between gap-2">
					<div>
						<p className="section-eyebrow">On-chain inventory</p>
						<h2 className="text-base font-bold">Stock held by treasury</h2>
					</div>
					<span className="text-[10px] font-semibold uppercase text-muted-foreground">
						{sleeve?.source === "live" ? "Live" : "Unavailable"}
						{sleeve?.gasFundingOpen != null
							? sleeve.gasFundingOpen
								? " · gas open"
								: " · gas closed"
							: ""}
					</span>
				</div>
				{isLoading ? (
					<div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
						{Array.from({ length: 5 }).map((_, i) => (
							<div key={i} className="skeleton-shimmer h-24" />
						))}
					</div>
				) : (
					<ul className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3">
						{(sleeve?.rows ?? []).map((r) => (
							<li
								key={r.label}
								className="card-surface flex items-start gap-3 p-4"
							>
								<TokenIcon
									token={normalizeTokenId(r.tokenId) ?? r.tokenId}
									size="md"
								/>
								<div className="min-w-0">
									<p className="text-[10px] uppercase text-muted-foreground">
										{r.label}
									</p>
									<p className="mt-0.5 font-mono-amount text-lg font-bold">
										{r.amount}
									</p>
									<p className="mt-1 text-[11px] text-muted-foreground">
										{r.note}
									</p>
									{r.tokenId === "MMK" ? (
										<p className="mt-1 text-[11px] text-muted-foreground">
											Only-up spot{" "}
											<span className="font-mono font-semibold text-foreground">
												{formatNum(mmkSpot, 4)}
											</span>{" "}
											USDM/MMK ·{" "}
											<Link
												to={ROUTES.governance}
												className="font-semibold text-primary underline"
											>
												Buy MMK
											</Link>
										</p>
									) : null}
								</div>
							</li>
						))}
					</ul>
				)}
			</section>

			<section
				id="section-agents"
				className="relative scroll-mt-24 overflow-hidden rounded-2xl border-2 border-yellow-500/50 bg-black p-5 sm:p-6"
			>
				<div
					className="pointer-events-none absolute inset-0 opacity-[0.07]"
					style={{
						backgroundImage:
							"linear-gradient(rgba(250,204,21,0.4) 1px, transparent 1px), linear-gradient(90deg, rgba(250,204,21,0.4) 1px, transparent 1px)",
						backgroundSize: "24px 24px",
					}}
				/>
				<div className="relative space-y-4">
					<p className="font-mono text-[10px] font-bold uppercase tracking-wider text-yellow-400/80">
						Agent Layer · Access
					</p>
					<div className="flex flex-wrap items-start gap-3">
						<div className="flex size-11 items-center justify-center rounded-xl border border-yellow-500/40 bg-yellow-400/10">
							<KeyRound className="size-5 text-yellow-400" />
						</div>
						<div className="min-w-0 flex-1">
							<h2 className="font-mono text-lg font-bold text-yellow-400">
								Same desk via API keys
							</h2>
							<p className="mt-1 max-w-xl font-mono text-sm text-yellow-400/70">
								Bots call{" "}
								<span className="text-yellow-300">SwapUsdmForAsset</span> /{" "}
								<span className="text-yellow-300">SwapAssetForUsdm</span> on
								agent_gateway (VaultWrite or All). Fee → surplus.
							</p>
						</div>
					</div>
					<div className="flex flex-wrap gap-2">
						<Link
							to={ROUTES.agents}
							className="inline-flex min-h-11 items-center gap-2 rounded-md border border-yellow-400 bg-yellow-400 px-4 font-mono text-sm font-bold text-black transition hover:bg-yellow-300"
						>
							<Zap className="size-4" />
							Open Agent Layer
						</Link>
						<Link
							to={docsArticlePath("treasury", "liquidity-sleeve")}
							className="inline-flex min-h-11 items-center rounded-md border border-yellow-500/40 px-4 font-mono text-sm font-semibold text-yellow-400 hover:bg-yellow-400/10"
						>
							Docs
						</Link>
					</div>
				</div>
			</section>

			<ConfirmSheet
				open={confirmOpen}
				onOpenChange={setConfirmOpen}
				title={`${buyAsset ? "Buy" : "Sell"} ${asset}`}
				description={`Pay ${amount} ${payToken} → receive ~${quote.out.toFixed(buyAsset ? 6 : 4)} ${recvToken}. Fee ~${quote.fee.toFixed(4)} USDM to surplus.`}
				confirmLabel={pending ? "Confirming…" : "Confirm desk trade"}
				onConfirm={() => {
					void executeDesk();
				}}
				isPending={pending}
				confirmDisabled={pending}
			/>
		</div>
	);
}

function sleeveInventory(
	sleeve:
		| {
				opsIcp: number;
				ckBtc: number;
				ckEth: number;
		  }
		| undefined,
	asset: DeskAsset,
): number {
	if (!sleeve) return 0;
	if (asset === "ICP") return sleeve.opsIcp;
	if (asset === "ckBTC") return sleeve.ckBtc;
	return sleeve.ckEth;
}
